import { useEffect, useMemo, useRef, useState } from "react";

import { useLazyRetrieveReservationQuery } from "../../../../../store/services/ReservationService";
import { hideModal } from "../../../../../store/slices/modalSlice";
import { resetReservationToInitial } from "../../../../../store/slices/paymentModalSlice";
import { showToast } from "../../../../../store/slices/toastSlice";
import { useTypedDispatch, useTypedSelector } from "../../../../../store/store";
import citizenshipList from "../../../../../utils/constants/citizenshipList";
import TOAST from "../../../../../utils/constants/toast";
import { currencyToFormat } from "../../../../../utils/helpers/currency";
import PaymentProviderRenderer from "../../../../payments";
import Alert from "../../../../shared/Alert";
import Checkbox from "../../../../shared/Checkbox";
import Input from "../../../../shared/Input";
import SearchInput from "../../../../shared/SearchInput";
import LoadingContainer from "../../../LoadingContainer";

import styles from "./index.module.scss";

const COUNTRY_LIST = citizenshipList
  .map((el) => ({
    label: el.country,
    value: el.iso_2,
  }))
  .sort((a, b) => a.label.toLowerCase().localeCompare(b.label.toLowerCase()));

function PaymentModal() {
  const [getReservation] = useLazyRetrieveReservationQuery();
  const dispatch = useTypedDispatch();

  const { reservation } = useTypedSelector((state) => state.paymentModal);
  const { type, data: pnr } = useTypedSelector((state) => state.modal);
  const { source } = useTypedSelector((state) => state.session);

  const { agency: channel_partner, agent: agency } = useTypedSelector(
    (state) => state.session,
  );

  const { payment_instruction_info, payment_provider } = useTypedSelector(
    (state) => state.environment,
  );

  const [countryListForm, setCountryListForm] = useState(COUNTRY_LIST);
  const [amount, setAmount] = useState({ value: 0, errorMessage: "" });
  const [isLoading, setIsLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [country, setCountry] = useState("");
  const [state, setState] = useState("");
  const [city, setCity] = useState("");
  const [zip, setZip] = useState("");
  const [max, setMax] = useState(0);
  const [min, setMin] = useState(0);

  const totalRemaining = +reservation.total_price - +reservation.total_paid;

  const isLoadedRef = useRef<boolean>(false);

  const handleAmountChange = ({ value }: { value: string }) => {
    const numValue = +value;

    let errorMessage = "";
    if (numValue < min) {
      errorMessage = `Min amount is ${min}`;
    }

    if (numValue > max) {
      errorMessage = `Max amount is ${max}`;
    }

    setAmount({ value: numValue, errorMessage });
  };

  const handleInputChange = ({
    value,
    valueKey,
  }: {
    value: string;
    valueKey: string;
  }) => {
    if (valueKey === "country") {
      setCountry(value);
    }
  };

  const handleSearchInputChange = ({
    value,
    valueKey,
  }: {
    value: string;
    valueKey?: string;
  }) => {
    switch (valueKey) {
      case "country": {
        const filterCountryResult = COUNTRY_LIST.filter(
          (countryLabel) =>
            countryLabel.label.toLowerCase().includes(value.toLowerCase()) ||
            countryLabel.value.toLowerCase().includes(value.toLowerCase()),
        );
        setCountryListForm(filterCountryResult);
        break;
      }

      default:
        break;
    }
  };

  const initReservation = () => {
    const depositMin =
      totalRemaining !== 0 && reservation.payment_schedule
        ? Math.min(
            ...reservation.payment_schedule.map((schedule) => +schedule.amount),
          )
        : 0;

    const initialValue = +reservation.next_payment_outstanding;
    let minVal = 0;

    if (reservation.status === "option") {
      minVal = depositMin;
    } else if (reservation.status === "confirmed") {
      minVal = 1;
    }

    setMin(minVal);
    setMax(totalRemaining);
    setAmount({ value: initialValue, errorMessage: "" });
  };

  const leadPassengersAddress = () => {
    const leadGuest = reservation.sailings?.[0]?.cabins?.[0]?.guests?.[0];
    if (leadGuest) {
      setFirstName(leadGuest.given_name);
      setLastName(leadGuest.lastname);
      setCity(leadGuest.city);
      setState(leadGuest.state);
      setZip(leadGuest.zip_code);
      setCountry(leadGuest.country);
    }
  };

  useEffect(() => {
    (async () => {
      if (pnr && !isLoadedRef.current) {
        await getReservation({ pnr, channel_partner, agency });
        isLoadedRef.current = true;
      }
    })();
  }, [pnr, channel_partner, agency, getReservation]);

  useEffect(() => {
    if (reservation.pnr) {
      initReservation();
      leadPassengersAddress();
    }
  }, [reservation]);

  useEffect(() => {
    if (type !== "" && reservation.pnr !== "") {
      setTimeout(() => {
        setIsLoading(false);
      }, 500);
    }
  }, [type, reservation]);

  useEffect(() => {
    if (type === "") {
      dispatch(resetReservationToInitial());
      isLoadedRef.current = false;
    }
  }, [type, dispatch]);

  const handlePaymentSuccess = () => {
    dispatch(hideModal());
    dispatch(
      showToast({
        type: TOAST.SUCCESS_TYPE,
        message: "Payment success",
        duration: TOAST.DEFAULT_DURATION,
      }),
    );
  };

  const handlePaymentFail = (message?: string) => {
    dispatch(
      showToast({
        type: TOAST.ERROR_TYPE,
        message: message || "Payment failed",
        duration: TOAST.DEFAULT_DURATION,
      }),
    );
  };

  const billingDetails = useMemo(() => {
    return {
      name: `${firstName} ${lastName}`,
      address: {
        city,
        state,
        postal_code: zip,
        country,
      },
    };
  }, [firstName, lastName, city, state, zip, country]);

  return (
    <LoadingContainer isLoading={isLoading}>
      <div className={styles.container}>
        <div className={styles.alerts}>
          {Boolean(payment_instruction_info) && (
            <Alert message={payment_instruction_info} />
          )}

          {Boolean(max && reservation.currency) && (
            <Alert
              message={`Balance Remaining: ${currencyToFormat(
                max,
                reservation.currency,
              )} ${reservation.currency}`}
            />
          )}
        </div>

        <div className={styles.form}>
          <div className={styles.inputs}>
            <Input
              className={styles.input}
              value={`${amount.value}`}
              label="Amount"
              placeholder="Enter amount"
              type="number"
              errorMessage={amount.errorMessage}
              onChange={handleAmountChange}
              showError
            />
          </div>

          <PaymentProviderRenderer
            mode="modal"
            hideTitleAndPriceType
            amount={amount.value}
            currency={reservation.currency}
            pnr={pnr}
            reservation={reservation}
            source={source}
            billingDetails={billingDetails}
            onSuccess={handlePaymentSuccess}
            onFail={handlePaymentFail}
          />

          {payment_provider !== "stripe" && (
            <>
              <Checkbox
                label="not the same address as lead guest"
                value={showForm}
                onChange={() => setShowForm((prev) => !prev)}
              />

              {showForm && (
                <>
                  <div className={styles.inputsDate}>
                    <Input
                      className={styles.input}
                      value={firstName}
                      label="First Name"
                      placeholder="Enter First Name"
                      onChange={({ value }) => setFirstName(value)}
                    />

                    <Input
                      className={styles.input}
                      value={lastName}
                      label="Last Name"
                      placeholder="Enter Last Name"
                      onChange={({ value }) => setLastName(value)}
                    />
                  </div>

                  <div className={styles.inputsDate}>
                    <Input
                      className={styles.input}
                      value={city}
                      label="City"
                      placeholder="Enter City"
                      onChange={({ value }) => setCity(value)}
                    />

                    <Input
                      className={styles.input}
                      value={state}
                      label="State"
                      placeholder="Enter State"
                      onChange={({ value }) => setState(value)}
                    />
                  </div>

                  <div className={styles.inputsDate}>
                    <Input
                      className={styles.input}
                      value={zip}
                      label="Zip Code"
                      placeholder="Enter Zip Code"
                      onChange={({ value }) => setZip(value)}
                    />

                    <SearchInput
                      className={styles.input}
                      value={country}
                      valueKey="country"
                      displayKey="label"
                      results={countryListForm}
                      label="country"
                      placeholder="Select a country"
                      isMultiple={false}
                      onChange={handleSearchInputChange}
                      onChosenChange={({ value, valueKey }) =>
                        handleInputChange({ value: value.label, valueKey })
                      }
                      isRequired
                    />
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </LoadingContainer>
  );
}

export default PaymentModal;
